/*
  Inner Botanica – The Letter (partner-letter): content, lexicons, and copy.
  window.IB_PL_DATA is consumed by partner-letter.js.

  What this tool does: a warm intake interview in her voice, composed — fully
  client-side, no backend, no AI — into a heartfelt letter she can give to her
  partner so they understand what she's going through. Physical symptoms, the
  emotional weather, the everyday things that get under her skin when she's on
  edge, what actually helps, and concrete ways her partner can lighten the load.

  CONTRACT: chip/option `id` values are encoded into shared ?k= links and are
  permanent once live. Labels and letter fragments may change freely; ids must
  never be renamed or reused. Bump `version` if the payload shape changes.

  Voice: drawn from the Inner Botanica VSL + 7-Symptom landing copy —
  "You're not imagining it. You're not being dramatic." · "You snap, then
  immediately regret it." · "the quiet grief of not feeling like myself" ·
  "You're not alone, and you're definitely not crazy." Every user-facing string here is
  mirrored in docs/partner-letter-STRINGS.md for review. Her free text is never
  paraphrased — it renders verbatim.
*/
window.IB_PL_DATA = (function () {
  "use strict";

  /* ============================================================ LEXICONS
     Each item: { id, label, letter }.
       label  — what she taps in the interview (her language)
       letter — a first-person fragment that drops into a joined sentence
                inside the letter (lowercase noun/verb phrase, no trailing
                punctuation). Composition adds the lead-in and the commas. */

  /* S2 — Physical: what her body has been doing. */
  var PHYS = [
    { id: "hot_flashes",  label: "Hot flashes",                        letter: "heat that rolls up out of nowhere" },
    { id: "night_sweats", label: "Night sweats",                       letter: "waking up drenched in the middle of the night" },
    { id: "sleep",        label: "Can't sleep / waking at 3am",        letter: "lying awake at 3am even though I'm exhausted" },
    { id: "fatigue",      label: "Tired no matter how much I sleep",   letter: "a tiredness that sleep doesn't touch" },
    { id: "brain_fog",    label: "Brain fog / losing words",           letter: "losing words in the middle of a sentence" },
    { id: "palpitations", label: "Heart racing / pounding",            letter: "my heart pounding for no reason I can find" },
    { id: "headaches",    label: "Headaches / migraines",              letter: "headaches that settle in and won't lift" },
    { id: "aches",        label: "New aches in my body",               letter: "aches in my body that are new to me" },
    { id: "weight",       label: "A body that feels unfamiliar",       letter: "a body that suddenly feels unfamiliar and uncooperative" },
    { id: "nausea",       label: "Waves of nausea / dizziness",        letter: "waves of nausea or dizziness that come and go" },
    { id: "libido",       label: "Intimacy feels far away",            letter: "a distance from intimacy that has nothing to do with you" },
    { id: "cycle",        label: "My cycle has gone unpredictable",    letter: "a cycle that's gone completely unpredictable" }
  ];

  /* S3 — Emotional weather: how it feels on the inside. */
  var EMO = [
    { id: "on_edge",     label: "On edge for no clear reason",         letter: "on edge for no reason I can point to" },
    { id: "short_fuse",  label: "My patience is paper-thin",           letter: "a patience that's thinner than it has ever been" },
    { id: "anxious",     label: "Anxiety out of nowhere",              letter: "anxiety that arrives without an invitation" },
    { id: "tearful",     label: "Crying easily",                       letter: "tears that show up before I even understand why" },
    { id: "flat",        label: "Flat / numb / behind glass",          letter: "days where I feel flat, like I'm watching my life from behind glass" },
    { id: "overwhelmed", label: "Small things feel overwhelming",      letter: "small things suddenly feeling like far too much" },
    { id: "not_myself",  label: "I don't feel like myself",            letter: "the quiet grief of not quite feeling like myself" },
    { id: "reactive",    label: "Reacting to things that never used to bother me", letter: "reacting to things that never used to touch me" },
    { id: "doubt",       label: "Second-guessing everything",          letter: "second-guessing myself in a way I never used to" },
    { id: "lonely",      label: "Lonely even when I'm not alone",      letter: "a loneliness that shows up even when you're right beside me" }
  ];

  /* S4 — Triggers: the everyday things that get under her skin when she's
     already on edge. This is the heart of the letter. */
  var TRIG = [
    { id: "chewing",     label: "The sound of chewing",                letter: "the sound of chewing" },
    { id: "breathing",   label: "Loud breathing",                      letter: "loud breathing" },
    { id: "joke_crit",   label: "A criticism dressed up as a joke",    letter: "a criticism dressed up as a joke — even a small one, even when you mean nothing by it" },
    { id: "fixing",      label: "Being handed a solution when I wanted to be heard", letter: "being handed a solution when all I wanted was to be heard" },
    { id: "interrupted", label: "Being interrupted",                   letter: "being interrupted mid-thought" },
    { id: "whats_wrong", label: "“What's wrong?” in a certain tone", letter: "being asked “what's wrong” in a certain tone" },
    { id: "noise",       label: "Too much noise or chaos at once",     letter: "too much noise or chaos happening at once" },
    { id: "mess",        label: "Mess I didn't make",                  letter: "a mess I didn't make and can't keep up with" },
    { id: "rushed",      label: "Being rushed",                        letter: "being rushed when I'm already behind" },
    { id: "touched",     label: "Being touched when I'm overstimulated", letter: "being touched when my skin already feels like too much" },
    { id: "unseen",      label: "Doing everything and feeling invisible", letter: "doing everything and feeling invisible while I do it" },
    { id: "questions",   label: "A lot of questions when I've got nothing left", letter: "a string of questions when I have nothing left to give" },
    { id: "plans",       label: "Plans changing at the last minute",   letter: "plans changing at the last minute" }
  ];

  /* S5 — What it's NOT. Reassurance. Default-selected; she keeps what's true. */
  var NOTREASONS = [
    { id: "not_you",      label: "It's not because of you",            letter: "None of this is because of you", on: true },
    { id: "still_love",   label: "I haven't stopped loving you",       letter: "I haven't stopped loving you — not for a single second", on: true },
    { id: "not_pushing",  label: "I'm not trying to push you away",    letter: "I'm not trying to push you away, even on the days it looks exactly like that", on: true },
    { id: "not_fight",    label: "I don't actually want to fight",     letter: "I don't actually want to fight — I want to feel safe enough to fall apart", on: true },
    { id: "not_ungrateful", label: "I'm not ungrateful",               letter: "I'm not ungrateful — I do see everything you do", on: false },
    { id: "coming_back",  label: "The woman you know isn't gone",      letter: "The woman you fell for isn't gone — she's just underwater right now", on: true }
  ];

  /* S6 — What actually helps in the moment. */
  var HELP = [
    { id: "sit",        label: "Just sit with me — don't fix it",      letter: "just sit with me without trying to fix it" },
    { id: "listen",     label: "Let me vent without solving it",       letter: "let me vent without turning it into a problem to solve" },
    { id: "water",      label: "A glass of water and some quiet",      letter: "hand me a glass of water and give me a little quiet" },
    { id: "reassure",   label: "Remind me I'm not crazy",             letter: "remind me I'm not crazy and that it always passes" },
    { id: "hug_ask",    label: "Offer a hug — but ask first",          letter: "offer a hug, but ask first — some days my skin can't take it" },
    { id: "lower",      label: "Turn down the noise / lights / TV",    letter: "turn down the noise, the lights, the TV" },
    { id: "space",      label: "Give me 20 minutes, then come back",   letter: "give me twenty minutes alone and then come back for me" },
    { id: "patience",   label: "Meet my snap with patience",          letter: "meet my snap with patience instead of a snap back" },
    { id: "name_it",    label: "Gently ask if it's “one of those days”", letter: "gently ask “is this one of those days?” so I don't have to explain it" },
    { id: "stay",       label: "Just stay — even if we're not talking", letter: "stay in the room with me, even if neither of us says a word" }
  ];

  /* S7 — Lighten the load: recurring things he can quietly take off her plate. */
  var LOAD = [
    { id: "meals",       label: "Handle dinner — just decide, don't ask me", letter: "just handle dinner — decide it and do it, without asking me what I want" },
    { id: "kids_out",    label: "Take the kids out so the house goes quiet", letter: "take the kids out for an hour so the house can go quiet" },
    { id: "bedtime",     label: "Own bedtime tonight, start to finish", letter: "own bedtime tonight, start to finish" },
    { id: "kitchen",     label: "Clear the kitchen without being asked", letter: "clear the kitchen without being asked" },
    { id: "mental_list", label: "Don't make me delegate — just pick something and do it", letter: "not wait for me to hand out jobs — pick something and just do it, because being the one who has to delegate is its own kind of exhausting" },
    { id: "calendar",    label: "Keep track of the calendar / appointments", letter: "keep track of the calendar and the appointments for a while" },
    { id: "morning",     label: "Take the morning so I get 30 minutes",  letter: "take the morning routine so I get thirty minutes that are only mine" },
    { id: "errands",     label: "Run the errands this week",           letter: "run the errands this week without a list from me" },
    { id: "decisions",   label: "Make the small decisions for a bit",   letter: "make the small decisions for a little while — I am all out of decisions" },
    { id: "night_off",   label: "One evening where no one needs me",    letter: "give me one evening where no one needs a single thing from me" }
  ];

  /* S8 — What I most need you to know. Kept punchy (max 3). Falls back to a
     default line if she selects none. */
  var CORE = [
    { id: "doing_best", label: "I'm doing the best I can",             letter: "I'm doing the very best I can with a body that changed the rules on me" },
    { id: "not_enemy",  label: "I'm not the enemy",                    letter: "I'm not the enemy, even on the days it feels like we're on opposite sides" },
    { id: "miss_me",    label: "I miss myself too",                    letter: "I miss myself too — more than you could possibly know" },
    { id: "scared",     label: "Part of me is scared",                 letter: "part of me is scared, and I don't always know how to say that out loud" },
    { id: "becoming",   label: "The real me is still in here",          letter: "the real me is still in here — I'm going through something that's changing me, but I haven't gone anywhere" },
    { id: "with_you",   label: "I'd rather go through this with you",  letter: "I would so much rather go through this with you than around you" },
    { id: "team",       label: "I need you on my team",                letter: "I need you on my team — not on the sidelines, and not across the ring" }
  ];
  var CORE_FALLBACK = "the real me is still in here, and so are we — I'm just going through something that's changing me, and I'd rather you were beside me for it";

  /* ============================================================ LETTER COPY
     The composition engine (partner-letter.js) assembles paragraphs from the
     selections above plus these tone-dependent bookends and per-block lead-ins.
     `{name}` is replaced with the partner's name; `{trig1}` with a representative
     trigger for the "it isn't really about…" line. */

  var TONES = [
    { id: "tender",     label: "Tender", hint: "Soft and open-hearted" },
    { id: "plain",      label: "Plainspoken", hint: "Direct and grounded" },
    { id: "light",      label: "A little lightness", hint: "Warm, with a touch of humor" }
  ];

  var LETTER = {
    /* salutation */
    salutation: function (name) { return name ? "Dear " + name + "," : "My love,"; },

    /* opening paragraph by tone */
    opening: {
      tender: "I've started this letter in my head a hundred times. It turns out it's easier to write than to say out loud, so here it is on paper. Please read it slowly, and read it the way I mean it — gently.",
      plain:  "There's something I've been trying to explain and keep getting wrong in the moment. It comes out easier on paper, so I wrote it down. I'm not asking you to fix anything here — I just want you to understand it.",
      light:  "I'm writing this down because every time I try to say it out loud it comes out as a sigh, a slammed cabinet, or tears over something ridiculous like the dishwasher. So — a letter. Bear with me; I promise there's love underneath all of it."
    },

    /* block lead-ins. Each takes the joined fragment list. */
    physLead:  "Lately my body hasn't felt like mine. There's ",
    physTail:  ". It's not in my head — it's happening in my body, and it's real.",

    emoLead:   "Underneath the surface, it's even harder to explain. I've been carrying ",
    emoTail:   ". Some days I'm completely fine. Other days everything feels louder, harder, and less predictable — even to me. I'm not imagining it, and I'm not being dramatic.",

    /* the heart block */
    trigLead:  "Here's the part that's hardest to put into words. When I'm already on edge, the smallest things can set me off — ",
    trigMid:   "I know exactly how that sounds. In a calmer moment I'd laugh at it too. But in that moment it doesn't feel small; it lands like the last straw on a day that already had too many. It isn't really about ",
    trigMidFallback: "the thing itself",
    trigTail:  ". It's that my nervous system has no room left in it. I'm not choosing it and I'm truly not enjoying it — and more often than not, I'm the one lying awake afterward, feeling terrible about how I reacted.",

    notLead:   "So before anything else, I need you to hear this clearly: ",
    notTail:   ".",

    helpLead:  "When it happens, here's what actually helps — probably more than you'd think. You can ",
    helpTail:  ". You don't have to make it go away. You just have to not leave me alone inside it.",

    loadLead:  "And when you can see I'm underwater, the kindest thing isn't to ask how to help — it's to quietly take something off my plate. Things like: ",
    loadTail:  ". The load doesn't get lighter when I have to hand it out piece by piece. It gets lighter when someone just picks it up.",

    coreLead:  "If you remember nothing else from this whole letter, remember this: ",
    coreTail:  ".",

    /* her verbatim PS */
    psLead:    "One more thing, in my own words: ",

    /* closing paragraph by tone */
    closing: {
      tender: "I'm still the same person you know, even on the days I don't act like her — I'm just in the middle of something that's changing me in ways I don't fully understand yet. I'd so much rather go through it with you than have you watch me from across the room. Thank you for reading all the way to the end — that already means more than you know.",
      plain:  "None of this is a complaint about you. It's a map. Now you have it. I didn't write it to make you feel bad — I wrote it because I'd rather you saw the whole picture than kept guessing at the parts that spill out sideways.",
      light:  "So that's the owner's manual for the current model of me. The warranty's still good. Batteries occasionally not included. Thank you for reading it — and for still being here, even on the dishwasher days."
    },

    /* signature */
    signOff: "All my love,",
    signFallback: "Me"
  };

  /* ============================================================ SCREENS
     Rendered by generic renderers in partner-letter.js, in array order. */
  var SCREENS = [
    {
      id: "who", type: "identity",
      prompt: "First — who is this letter for?",
      sub: "Just so it sounds like it's coming from you, to them. Both are optional."
    },
    {
      id: "phys", type: "chips", key: "phys",
      prompt: "What's your body been putting you through?",
      sub: "Tap anything that's been true. Skip anything that isn't.",
      chips: PHYS, min: 0
    },
    {
      id: "emo", type: "chips", key: "emo",
      prompt: "And on the inside — how has it actually felt?",
      sub: "The part that's harder to say out loud.",
      chips: EMO, min: 0
    },
    {
      id: "trig", type: "chips", key: "trig",
      prompt: "When you're already on edge, what gets under your skin?",
      sub: "The little things that land like the last straw. Nothing here is silly — tap all of them.",
      chips: TRIG, min: 0
    },
    {
      id: "not", type: "chips", key: "not",
      prompt: "What do you most want them to know it's NOT about?",
      sub: "We've pre-selected the ones women tell us most. Keep the ones that are true for you.",
      chips: NOTREASONS, min: 0, preselect: true
    },
    {
      id: "help", type: "chips", key: "help",
      prompt: "In the hard moments, what actually helps?",
      sub: "So they're not left guessing what to do.",
      chips: HELP, min: 0
    },
    {
      id: "load", type: "chips", key: "load",
      prompt: "When you're underwater, how could they lighten the load?",
      sub: "Concrete things they can quietly take off your plate — no asking required.",
      chips: LOAD, min: 0
    },
    {
      id: "core", type: "chips", key: "core",
      prompt: "If they remember only one thing…",
      sub: "Choose up to three. These become the heart of your letter.",
      chips: CORE, min: 0, max: 3
    },
    {
      id: "tone", type: "single", key: "tone",
      prompt: "How should it sound?",
      sub: "You can change every word before you send it.",
      options: TONES
    },
    {
      id: "ps", type: "text", key: "ps",
      prompt: "Anything you want to say in your own words?",
      sub: "Optional. Whatever you write here goes into the letter exactly as you type it.",
      placeholder: "In your own words, if you want to…",
      maxlen: 600,
      skipLabel: "Nothing to add"
    }
  ];

  /* ============================================================ STRINGS */
  var STRINGS = {
    back: "Back",
    next: "Next",
    skip: "Skip",
    chooseHint: "Tap all that feel true.",
    resumeYes: "Pick up where I left off",
    fromLabel: "Your name",
    fromPlaceholder: "e.g. Sarah",
    toLabel: "Their name",
    toPlaceholder: "e.g. Michael",
    transition: "Putting it into words…",
    reveal: {
      title: "Here it is — in your words.",
      sub: "Read it. Every line of it is yours. Change anything that doesn't sound like you, then keep it however you'd like.",
      editHint: "Edit the wording",
      doneHint: "Done editing",
      letterHeading: "A letter to help you understand"
    },
    actions: {
      download: "Download as PDF",
      share: "Email / text it to myself",
      edit: "Edit my answers",
      print: "or print this page"
    },
    share: {
      title: "A letter for you to read",
      copied: "Link copied. Text or email it to yourself — opening it on any device brings the letter back, edits and all.",
      copyFail: "Press and hold to copy your link:"
    },
    download: {
      iosHint: "If the PDF opened in a new tab: tap the share icon, then “Save to Files.”",
      blocked: "This in-app browser won't save files. Tap “Email / text it to myself” and open the link in Safari or Chrome — or use “or print this page.”"
    },
    after: {
      head: "That took courage.",
      body: "However they read it, you did the brave thing — you let yourself be understood instead of hoping someone would guess. That matters. Be gentle with yourself today.",
      again: "Write another letter",
      downloadAgain: "Back to my letter"
    }
  };

  return {
    version: 1,
    phys: PHYS,
    emo: EMO,
    trig: TRIG,
    notReasons: NOTREASONS,
    help: HELP,
    load: LOAD,
    core: CORE,
    coreFallback: CORE_FALLBACK,
    tones: TONES,
    letter: LETTER,
    screens: SCREENS,
    strings: STRINGS
  };
})();
